import moment from 'moment-timezone';

/**
 * Human-readable UTC offset for an IANA zone.
 *
 * Offsets are derived from the zone's actual rules (via moment-timezone)
 * rather than hard-coded, so labels stay correct for zones that observe DST
 * (e.g. Los Angeles is UTC-8 in winter and UTC-7 in summer) and for zones
 * whose rules change (Chihuahua dropped DST in 2022).
 *
 * Zones with DST show both offsets, e.g. "UTC-8 / UTC-7 DST".
 * Zones without DST show a single offset plus "no DST".
 */
const formatOffset = (minutes: number): string => {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
};

export const getTimeZoneOffsetLabel = (zone: string): string => {
  const year = new Date().getFullYear();
  const jan = moment.tz({ year, month: 0, day: 15 }, zone).utcOffset();
  const jul = moment.tz({ year, month: 6, day: 15 }, zone).utcOffset();
  if (jan === jul) {
    return `${formatOffset(jan)}, no DST`;
  }
  const standard = Math.min(jan, jul);
  const daylight = Math.max(jan, jul);
  return `${formatOffset(standard)} / ${formatOffset(daylight)} DST`;
};

const rawTimeZones = [
  { value: "Africa/Abidjan", display: "Greenwich Mean Time (Abidjan)" },
  { value: "Africa/Accra", display: "Greenwich Mean Time (Accra)" },
  { value: "Africa/Addis_Ababa", display: "Eastern Africa Time (Addis Ababa)" },
  { value: "Africa/Algiers", display: "Central European Time (Algiers)" },
  { value: "Africa/Cairo", display: "Eastern European Time (Cairo)" },
  { value: "Africa/Casablanca", display: "Western European Time (Casablanca)" },
  { value: "Africa/Johannesburg", display: "South Africa Standard Time (Johannesburg)" },
  { value: "Africa/Lagos", display: "West Africa Time (Lagos)" },
  { value: "Africa/Nairobi", display: "East Africa Time (Nairobi)" },
  { value: "Africa/Tunis", display: "Central European Time (Tunis)" },

  { value: "America/Anchorage", display: "Alaska Time (Anchorage)" },
  { value: "America/Argentina/Buenos_Aires", display: "Argentina Time (Buenos Aires)" },
  { value: "America/Bogota", display: "Colombia Time (Bogota)" },
  { value: "America/Chicago", display: "Central Time (Chicago)" },
  { value: "America/Chihuahua", display: "Central Time (Chihuahua)" },
  { value: "America/Denver", display: "Mountain Time (Denver)" },
  { value: "America/Edmonton", display: "Mountain Time (Edmonton)" },
  { value: "America/Halifax", display: "Atlantic Time (Halifax)" },
  { value: "America/Los_Angeles", display: "Pacific Time (Los Angeles)" },
  { value: "America/Manaus", display: "Amazon Time (Manaus)" },
  { value: "America/Mexico_City", display: "Central Time (Mexico City)" },
  { value: "America/Montevideo", display: "Uruguay Time (Montevideo)" },
  { value: "America/New_York", display: "Eastern Time (New York)" },
  { value: "America/Phoenix", display: "Mountain Time (Phoenix)" },
  { value: "America/Rio_Branco", display: "Acre Time (Rio Branco)" },
  { value: "America/Santiago", display: "Chile Time (Santiago)" },
  { value: "America/Sao_Paulo", display: "Brasilia Time (São Paulo)" },
  { value: "America/St_Johns", display: "Newfoundland Time (St. John's)" },
  { value: "America/Tijuana", display: "Pacific Time (Tijuana)" },
  { value: "America/Toronto", display: "Eastern Time (Toronto)" },
  { value: "America/Vancouver", display: "Pacific Time (Vancouver)" },
  { value: "America/Winnipeg", display: "Central Time (Winnipeg)" },

  { value: "Asia/Bangkok", display: "Indochina Time (Bangkok)" },
  { value: "Asia/Dubai", display: "Gulf Standard Time (Dubai)" },
  { value: "Asia/Hong_Kong", display: "Hong Kong Time" },
  { value: "Asia/Irkutsk", display: "Irkutsk Time" },
  { value: "Asia/Istanbul", display: "Turkey Time (Istanbul)" },
  { value: "Asia/Jerusalem", display: "Israel Standard Time (Jerusalem)" },
  { value: "Asia/Kolkata", display: "India Standard Time (Kolkata)" },
  { value: "Asia/Kuwait", display: "Arabia Standard Time (Kuwait)" },
  { value: "Asia/Manila", display: "Philippine Time (Manila)" },
  { value: "Asia/Novosibirsk", display: "Novosibirsk Time" },
  { value: "Asia/Seoul", display: "Korea Standard Time (Seoul)" },
  { value: "Asia/Shanghai", display: "China Standard Time (Shanghai)" },
  { value: "Asia/Singapore", display: "Singapore Time" },
  { value: "Asia/Tokyo", display: "Japan Standard Time (Tokyo)" },
  { value: "Asia/Vladivostok", display: "Vladivostok Time" },
  { value: "Asia/Yekaterinburg", display: "Yekaterinburg Time" },

  { value: "Australia/Adelaide", display: "Australian Central Time (Adelaide)" },
  { value: "Australia/Brisbane", display: "Australian Eastern Time (Brisbane)" },
  { value: "Australia/Melbourne", display: "Australian Eastern Time (Melbourne)" },
  { value: "Australia/Perth", display: "Australian Western Time (Perth)" },
  { value: "Australia/Sydney", display: "Australian Eastern Time (Sydney)" },

  { value: "Europe/Amsterdam", display: "Central European Time (Amsterdam)" },
  { value: "Europe/Athens", display: "Eastern European Time (Athens)" },
  { value: "Europe/Berlin", display: "Central European Time (Berlin)" },
  { value: "Europe/Brussels", display: "Central European Time (Brussels)" },
  { value: "Europe/Dublin", display: "Irish Standard Time (Dublin)" },
  { value: "Europe/Helsinki", display: "Eastern European Time (Helsinki)" },
  { value: "Europe/Kaliningrad", display: "Kaliningrad Time" },
  { value: "Europe/Lisbon", display: "Western European Time (Lisbon)" },
  { value: "Europe/London", display: "British Time (London)" },
  { value: "Europe/Madrid", display: "Central European Time (Madrid)" },
  { value: "Europe/Moscow", display: "Moscow Standard Time" },
  { value: "Europe/Oslo", display: "Central European Time (Oslo)" },
  { value: "Europe/Paris", display: "Central European Time (Paris)" },
  { value: "Europe/Prague", display: "Central European Time (Prague)" },
  { value: "Europe/Rome", display: "Central European Time (Rome)" },
  { value: "Europe/Stockholm", display: "Central European Time (Stockholm)" },
  { value: "Europe/Vienna", display: "Central European Time (Vienna)" },
  { value: "Europe/Warsaw", display: "Central European Time (Warsaw)" },
  { value: "Europe/Zurich", display: "Central European Time (Zurich)" },

  { value: "Pacific/Auckland", display: "New Zealand Time (Auckland)" },
  { value: "Pacific/Fiji", display: "Fiji Time" },
  { value: "Pacific/Honolulu", display: "Hawaii Time (Honolulu)" },
  { value: "Pacific/Midway", display: "Samoa Standard Time (Midway)" },
  { value: "Pacific/Tahiti", display: "Tahiti Time" }
];

export const timeZones = rawTimeZones.map(tz => ({
  value: tz.value,
  display: `${tz.display} (${getTimeZoneOffsetLabel(tz.value)})`
}));

// Mapping of country codes to their associated timezone values
export const countryTimezones: { [countryCode: string]: string[] } = {
  // United States
  'US': [
    'America/New_York',    // Eastern Time
    'America/Chicago',     // Central Time
    'America/Denver',      // Mountain Time
    'America/Phoenix',     // Mountain Standard Time (no DST)
    'America/Los_Angeles', // Pacific Time
    'America/Anchorage',   // Alaska Time
    'Pacific/Honolulu'     // Hawaii Time
  ],

  // Canada
  'CA': [
    'America/St_Johns',    // Newfoundland Time
    'America/Halifax',     // Atlantic Time
    'America/Toronto',     // Eastern Time
    'America/Winnipeg',    // Central Time
    'America/Edmonton',    // Mountain Time
    'America/Vancouver'    // Pacific Time
  ],

  // United Kingdom
  'GB': [
    'Europe/London'        // British Time
  ],

  // Australia
  'AU': [
    'Australia/Sydney',    // Eastern Time
    'Australia/Melbourne', // Eastern Time
    'Australia/Brisbane',  // Eastern Standard Time (no DST)
    'Australia/Adelaide',  // Central Time
    'Australia/Perth'      // Western Time
  ],

  // Mexico
  'MX': [
    'America/Mexico_City', // Central Time
    'America/Chihuahua',   // Central Standard Time (no DST since 2022)
    'America/Tijuana'      // Pacific Time
  ],

  // Italy
  'IT': [
    'Europe/Rome'          // Central European Time
  ],

  // France
  'FR': [
    'Europe/Paris'         // Central European Time
  ],

  // Germany
  'DE': [
    'Europe/Berlin'        // Central European Time
  ],

  // Spain
  'ES': [
    'Europe/Madrid'        // Central European Time
  ],

  // China
  'CN': [
    'Asia/Shanghai'        // China Standard Time
  ],

  // Japan
  'JP': [
    'Asia/Tokyo'           // Japan Standard Time
  ],

  // India
  'IN': [
    'Asia/Kolkata'         // India Standard Time
  ],

  // Brazil
  'BR': [
    'America/Sao_Paulo',   // Brasilia Time
    'America/Manaus',      // Amazon Time
    'America/Rio_Branco'   // Acre Time
  ],

  // Russia (partial list - Russia has many timezones)
  'RU': [
    'Europe/Moscow',       // Moscow Time
    'Europe/Kaliningrad',  // Kaliningrad Time
    'Asia/Yekaterinburg',  // Yekaterinburg Time
    'Asia/Novosibirsk',    // Novosibirsk Time
    'Asia/Irkutsk',        // Irkutsk Time
    'Asia/Vladivostok'     // Vladivostok Time
  ]
};

export const getTimeZoneDisplay = (value: string): string => {
  const timezone = timeZones.find(tz => tz.value === value);
  return timezone ? timezone.display : value;
};

// Function to filter timezones by country
export const getTimezonesByCountry = (countryCode: string): any[] => {
  if (!countryCode || !countryTimezones[countryCode]) {
    return timeZones; // Return all timezones if no country selected or country not in mapping
  }

  // Filter timezones based on the country's timezone values
  return timeZones.filter(tz => countryTimezones[countryCode].includes(tz.value));
};
